import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import PipelineTimeline from "@/components/PipelineTimeline";
import { api } from "@/lib/api";
import { STAGES, DOC_STATUSES } from "@/lib/stages";
import { commissionFor } from "@/lib/commissions";

export default function AdminDealDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [deal, setDeal] = useState(null);
  const [error, setError] = useState("");
  const [sanitizedView, setSanitizedView] = useState(null);
  const [showSanitized, setShowSanitized] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/deals/${id}`).then((r) => setDeal(r.data)).catch(() => setError("Not found"));
    api.get(`/admin/deals/${id}/sanitized`).then((r) => setSanitizedView(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const moveStage = async (stage) => {
    await api.patch(`/admin/deals/${id}/stage`, { stage });
    load();
  };
  const setDocStatus = async (doc, status) => {
    await api.patch(`/admin/deals/${id}/document`, { doc, status, note: "" });
    load();
  };
  const generateDoc = async (doc) => {
    try {
      await api.post(`/admin/deals/${id}/documents/${doc}/generate`);
      load();
    } catch (e) {
      window.console.error("Generation failed", e);
    }
  };
  const downloadDoc = async (doc) => {
    try {
      const res = await api.get(`/admin/deals/${id}/documents/${doc}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      window.console.error("Download failed", e);
    }
  };

  const downloadExecutedDoc = async (doc) => {
    try {
      const res = await api.get(`/admin/deals/${id}/documents/${doc}/executed-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      window.alert("Executed copy not available yet");
    }
  };

  const [sigDialog, setSigDialog] = useState(null);
  const openSigDialog = (doc) => setSigDialog({
    doc,
    seller_name: deal?.fco?.seller_origin || "",
    seller_email: "",
    busy: false,
  });
  const sendForSignature = async () => {
    if (!sigDialog) return;
    setSigDialog({ ...sigDialog, busy: true });
    try {
      await api.post(`/admin/deals/${id}/documents/${sigDialog.doc}/send-for-signature`, {
        seller_name: sigDialog.seller_name,
        seller_email: sigDialog.seller_email,
      });
      setSigDialog(null);
      load();
    } catch (e) {
      window.alert(e.response?.data?.detail || "Send failed");
      setSigDialog({ ...sigDialog, busy: false });
    }
  };

  const deleteDeal = async () => {
    if (!deal) return;
    const ok = window.confirm(
      `Permanently delete this deal?\n\n${deal.commodity}\n${deal.deal_id}\nBuyer: ${deal.user_email}\n\nThis cannot be undone.`
    );
    if (!ok) return;
    try {
      await api.delete(`/admin/deals/${id}`);
      navigate("/admin/deals", { replace: true });
    } catch (e) {
      window.alert("Delete failed: " + (e.response?.data?.detail || e.message));
    }
  };

  if (error) return <DashboardLayout><div className="p-12 text-danger">{error}</div></DashboardLayout>;
  if (!deal) return <DashboardLayout><div className="p-12 overline">Loading…</div></DashboardLayout>;

  return (
    <DashboardLayout testid="admin-deal-detail-page">
      <div className="p-8 md:p-12 max-w-7xl">
        <Link to="/admin/deals" className="link text-sub text-xs">← Back to deals</Link>
        <div className="mt-4 flex flex-wrap items-baseline justify-between gap-4">
          <div>
            <div className="overline">{deal.deal_id}</div>
            <h1 className="h-serif text-5xl text-ink mt-2">{deal.commodity}</h1>
            <div className="font-mono text-xs text-sub mt-2 tabular">
              {Number(deal.volume).toLocaleString()} {deal.unit} · ${Number(deal.target_price).toLocaleString()}/{deal.unit} · {deal.incoterm} · {deal.delivery_port}
            </div>
            <div className="text-sm text-sub mt-2">
              Buyer: <span className="text-ink">{deal.user_name}</span> · {deal.user_email} · {deal.company || "—"}
            </div>
          </div>
          <button onClick={() => setShowSanitized(!showSanitized)}
                  className="btn-ghost !py-2 !px-4 text-xs" data-testid="toggle-sanitized">
            {showSanitized ? "Show buyer view" : "Show sanitized (seller view)"}
          </button>
        </div>

        {showSanitized && sanitizedView && (
          <div className="mt-6 border-l-4 border-brass bg-parchment2 p-5" data-testid="sanitized-block">
            <div className="overline !text-brass">Sanitized — buyer identity stripped</div>
            <p className="text-xs text-sub mt-2">This is the version the platform forwards to vetted sellers.</p>
            <pre className="text-xs font-mono mt-3 overflow-x-auto bg-parchment p-4 border border-line">
{JSON.stringify(sanitizedView, null, 2)}
            </pre>
          </div>
        )}

        <section className="mt-10">
          <div className="overline">Pipeline</div>
          <PipelineTimeline stage={deal.stage} />
          <div className="mt-4 flex flex-wrap gap-1.5" data-testid="stage-advance">
            {STAGES.map((s) => (
              <button key={s.key} onClick={() => moveStage(s.key)}
                      disabled={deal.stage === s.key}
                      className={`text-xs font-mono uppercase px-3 py-1.5 border ${deal.stage === s.key ? "bg-oxblood text-parchment border-oxblood" : "border-line text-sub hover:text-ink"}`}
                      data-testid={`advance-${s.key}`}>
                {s.short}
              </button>
            ))}
          </div>
        </section>

        <section className="mt-12 grid md:grid-cols-3 gap-5" data-testid="admin-docs">
          {[
            { k: "ncnda", tier: "Tier 1", doc: "NCNDA" },
            { k: "imfpa", tier: "Tier 2", doc: "IMFPA" },
            { k: "spa",   tier: "Tier 3", doc: "SPA" },
          ].map(({ k, tier, doc }) => {
            const d = deal.documents?.[k] || { status: "pending" };
            const hasPdf = d.pdf_available;
            const rate = k === "imfpa" ? commissionFor(deal.commodity) : null;
            return (
              <div key={k} className="bg-parchment border border-line p-6" data-testid={`admin-doc-${k}`}>
                <div className="overline">{tier}</div>
                <h3 className="h-serif text-2xl text-ink mt-1">{doc}</h3>
                <div className="text-xs font-mono text-sub mt-1 uppercase">Status: {d.status}</div>
                {d.generated_at && (
                  <div className="text-[10px] font-mono text-sub mt-1">
                    Generated: {d.generated_at.slice(0, 19).replace("T", " ")}
                  </div>
                )}
                {rate && (
                  <div className="mt-4 border-l-2 border-oxblood bg-parchment2 px-3 py-3"
                       data-testid={`admin-rate-${k}`}>
                    <div className="overline">Schedule range · {rate.label}</div>
                    <div className="font-mono text-xs text-ink mt-1 tabular">
                      {rate.min} <span className="text-sub">–</span> {rate.max}
                    </div>
                    {deal.commission && deal.commission.rate_value !== undefined && deal.commission.rate_value !== null && (
                      <div className="mt-3 pt-3 border-t border-line" data-testid={`admin-negotiated-${k}`}>
                        <div className="overline !text-oxblood">Negotiated rate</div>
                        <div className="font-mono text-sm text-oxblood mt-1 tabular font-semibold">
                          {Number(deal.commission.rate_value).toLocaleString()} {deal.commission.rate_unit}
                        </div>
                        {deal.commission.total_estimate_usd && (
                          <div className="font-mono text-[10px] text-sub mt-1 tabular">
                            Est. total · ${Number(deal.commission.total_estimate_usd).toLocaleString()}
                            {deal.commission.lc_type ? ` · ${deal.commission.lc_type}` : ""}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <select className="mt-4 w-full font-mono text-xs uppercase bg-transparent border border-line px-2 py-2"
                        value={d.status} onChange={(e) => setDocStatus(k, e.target.value)}
                        data-testid={`admin-doc-status-${k}`}>
                  {DOC_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    onClick={() => generateDoc(k)}
                    className="flex-1 text-[11px] font-mono uppercase px-3 py-2 border border-oxblood text-oxblood hover:bg-oxblood hover:text-parchment transition-colors"
                    data-testid={`generate-${k}`}>
                    {hasPdf ? "Re-generate PDF" : "Generate draft PDF"}
                  </button>
                  {hasPdf && (
                    <button
                      onClick={() => downloadDoc(k)}
                      className="text-[11px] font-mono uppercase px-3 py-2 border border-brass text-brass hover:bg-brass hover:text-ink transition-colors"
                      data-testid={`download-${k}`}>
                      Open PDF ↗
                    </button>
                  )}
                </div>

                {hasPdf && (
                  <div className="mt-3 pt-3 border-t border-line" data-testid={`sig-block-${k}`}>
                    <div className="overline">E-signature · Dropbox Sign</div>
                    {!d.signature_request_id ? (
                      <button
                        onClick={() => openSigDialog(k)}
                        className="w-full mt-2 text-[11px] font-mono uppercase px-3 py-2 border border-ink text-ink hover:bg-ink hover:text-parchment transition-colors"
                        data-testid={`send-sig-${k}`}>
                        Send for signature →
                      </button>
                    ) : (
                      <>
                        <div className="mt-2 text-xs text-sub font-mono uppercase tracking-wider" data-testid={`sig-status-${k}`}>
                          Status: <span className="text-ink">{d.signature_status || "sent"}</span>
                          {d.signature_test_mode && <span className="ml-2 text-warn">TEST MODE</span>}
                        </div>
                        {(d.signers || []).map((s, i) => (
                          <div key={i} className="mt-1 text-[10px] font-mono text-sub">
                            {s.role}: {s.email} — {s.status}
                            {s.signed_at && ` (${s.signed_at.slice(0, 10)})`}
                          </div>
                        ))}
                        {d.executed_pdf_available && (
                          <button
                            onClick={() => downloadExecutedDoc(k)}
                            className="w-full mt-2 text-[11px] font-mono uppercase px-3 py-2 border border-success text-success hover:bg-success hover:text-parchment transition-colors"
                            data-testid={`download-executed-${k}`}>
                            Download signed copy ↗
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </section>

        {sigDialog && (
          <div className="fixed inset-0 bg-ink/60 flex items-center justify-center z-50 p-6"
               onClick={() => setSigDialog(null)} data-testid="sig-dialog">
            <div className="bg-parchment border border-line max-w-md w-full p-8"
                 onClick={(e) => e.stopPropagation()}>
              <div className="overline">Send for signature · Dropbox Sign</div>
              <h3 className="h-serif text-2xl text-ink mt-2">{sigDialog.doc.toUpperCase()}</h3>
              <p className="text-sub text-sm mt-3 leading-relaxed">
                Sends the current draft PDF to the buyer ({deal.user_email}) and the
                seller below via Dropbox Sign. Test mode is currently
                <span className="text-warn font-mono"> ON</span> — signatures will be watermarked and non-binding.
              </p>
              <div className="mt-5 space-y-3">
                <label className="block">
                  <span className="overline block mb-1">Seller name</span>
                  <input type="text" required className="input-line"
                         value={sigDialog.seller_name}
                         onChange={(e) => setSigDialog({ ...sigDialog, seller_name: e.target.value })}
                         data-testid="sig-seller-name" />
                </label>
                <label className="block">
                  <span className="overline block mb-1">Seller email</span>
                  <input type="email" required className="input-line"
                         value={sigDialog.seller_email}
                         onChange={(e) => setSigDialog({ ...sigDialog, seller_email: e.target.value })}
                         data-testid="sig-seller-email" />
                </label>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button className="btn-ghost text-sm" onClick={() => setSigDialog(null)}
                        data-testid="sig-cancel">
                  Cancel
                </button>
                <button className="btn-primary text-sm"
                        disabled={sigDialog.busy || !sigDialog.seller_name || !sigDialog.seller_email}
                        onClick={sendForSignature} data-testid="sig-confirm">
                  {sigDialog.busy ? "Sending…" : "Send for signature"}
                </button>
              </div>
            </div>
          </div>
        )}

        <section className="mt-12 grid md:grid-cols-2 gap-6">
          <FCORecorder
            key={`fco-${JSON.stringify(deal.fco) || "empty"}`}
            dealId={id} fco={deal.fco} unit={deal.unit} onSaved={load} />
          <CommissionEditor
            key={`comm-${JSON.stringify(deal.commission) || "empty"}`}
            dealId={id} commission={deal.commission} unit={deal.unit} onSaved={load} />
        </section>

        <section className="mt-12">
          <div className="overline">Stage history</div>
          <div className="border border-line bg-parchment mt-3 divide-y divide-line">
            {(deal.stage_history || []).map((h) => (
              <div key={`${h.at}-${h.stage}`} className="p-3 flex items-baseline justify-between text-xs">
                <span className="font-mono uppercase text-oxblood">{h.stage}</span>
                <span className="font-mono text-sub">{h.at?.slice(0, 19).replace("T", " ")}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-16 border-t border-danger/30 pt-8" data-testid="danger-zone">
          <div className="overline !text-danger">Danger zone</div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-4 bg-parchment border border-danger/40 p-5">
            <div>
              <h3 className="h-serif text-xl text-ink">Delete this deal</h3>
              <p className="text-sub text-sm mt-1 max-w-xl leading-relaxed">
                Permanently removes the deal, its FCO record, generated PDFs and stage history.
                The buyer&apos;s account is not affected.
              </p>
            </div>
            <button
              onClick={deleteDeal}
              className="text-xs font-mono uppercase px-4 py-2.5 border border-danger text-danger hover:bg-danger hover:text-parchment transition-colors"
              data-testid="delete-deal-btn"
            >
              Delete deal →
            </button>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}

function FCORecorder({ dealId, fco, unit, onSaved }) {
  const [form, setForm] = useState({
    seller_origin: fco?.seller_origin || "",
    pricing_per_unit: fco?.pricing_per_unit || "",
    allocation_capacity: fco?.allocation_capacity || "",
    validity_until: fco?.validity_until || "",
    notes: fco?.notes || "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/admin/deals/${dealId}/fco`, {
        ...form,
        pricing_per_unit: Number(form.pricing_per_unit),
        allocation_capacity: Number(form.allocation_capacity),
      });
      onSaved();
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="bg-parchment border border-line p-6" data-testid="fco-form">
      <div className="overline">Record FCO</div>
      <h3 className="h-serif text-2xl text-ink mt-1">Seller&apos;s Full Corporate Offer</h3>
      <div className="grid gap-4 mt-5">
        <Field label="Seller origin (confidential)">
          <input className="input-line" value={form.seller_origin}
                 onChange={(e) => setForm({ ...form, seller_origin: e.target.value })}
                 placeholder="Refinery / mill / origin — admin-only"
                 data-testid="fco-origin" required />
        </Field>
        <Field label={`Pricing (USD / ${unit})`}>
          <input type="number" min="0" step="any" className="input-line tabular" value={form.pricing_per_unit}
                 onChange={(e) => setForm({ ...form, pricing_per_unit: e.target.value })}
                 data-testid="fco-price" required />
        </Field>
        <Field label={`Allocation capacity (${unit})`}>
          <input type="number" min="0" step="any" className="input-line tabular" value={form.allocation_capacity}
                 onChange={(e) => setForm({ ...form, allocation_capacity: e.target.value })}
                 data-testid="fco-capacity" required />
        </Field>
        <Field label="Validity until">
          <input type="date" className="input-line" value={form.validity_until}
                 onChange={(e) => setForm({ ...form, validity_until: e.target.value })}
                 data-testid="fco-validity" />
        </Field>
        <Field label="Desk notes">
          <textarea rows={2} className="input-line resize-none" value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    data-testid="fco-notes" />
        </Field>
      </div>
      <button className="btn-primary mt-5" disabled={busy} data-testid="fco-submit">
        {busy ? "Saving…" : fco ? "Update FCO" : "Record FCO →"}
      </button>
    </form>
  );
}

function CommissionEditor({ dealId, commission, unit, onSaved }) {
  const [form, setForm] = useState({
    rate_value: commission?.rate_value || "",
    rate_unit: commission?.rate_unit || (unit === "BBL" ? "USD / BBL" : "USD cents / lb"),
    total_estimate_usd: commission?.total_estimate_usd || "",
    lc_type: commission?.lc_type || "DLC",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/admin/deals/${dealId}/commission`, {
        ...form,
        rate_value: Number(form.rate_value),
        total_estimate_usd: form.total_estimate_usd ? Number(form.total_estimate_usd) : null,
      });
      onSaved();
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="bg-parchment border border-line p-6" data-testid="commission-form">
      <div className="overline">IMFPA commission</div>
      <h3 className="h-serif text-2xl text-ink mt-1">Our take, lodged with the banks</h3>
      <div className="grid gap-4 mt-5">
        <Field label="Rate value">
          <input type="number" min="0" step="any" className="input-line tabular" value={form.rate_value}
                 onChange={(e) => setForm({ ...form, rate_value: e.target.value })}
                 data-testid="comm-rate" required />
        </Field>
        <Field label="Rate unit">
          <input className="input-line" value={form.rate_unit}
                 onChange={(e) => setForm({ ...form, rate_unit: e.target.value })}
                 placeholder="USD cents / lb · USD / BBL · USD / MT"
                 data-testid="comm-unit" />
        </Field>
        <Field label="Total commission estimate (USD)">
          <input type="number" min="0" step="any" className="input-line tabular" value={form.total_estimate_usd}
                 onChange={(e) => setForm({ ...form, total_estimate_usd: e.target.value })}
                 data-testid="comm-total" />
        </Field>
        <Field label="LC instrument">
          <select className="input-line" value={form.lc_type}
                  onChange={(e) => setForm({ ...form, lc_type: e.target.value })}
                  data-testid="comm-lc">
            {["DLC", "SBLC", "MT103", "Other"].map((o) => <option key={o}>{o}</option>)}
          </select>
        </Field>
      </div>
      <button className="btn-primary mt-5" disabled={busy} data-testid="comm-submit">
        {busy ? "Saving…" : "Save commission"}
      </button>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="overline block mb-1">{label}</span>
      {children}
    </label>
  );
}
